import React, { startTransition, useEffect, useState, useCallback } from 'react';
import { Button, Col, Form, Row, Space, notification } from 'antd';
import { EditFilled, CloseOutlined, CheckOutlined } from '@ant-design/icons';

import { useSelector, useDispatch } from 'react-redux';

import FormField from '../molecules/FormField';
import CustomInputField from '../atoms/CustomInputField';

import CustomButton from '../atoms/CustomButton';
import CustomSelectBox from '../atoms/CustomSelectBox';
import { getUnitListApi, getUnitListReset } from '../redux/slice/unit/getUnitListSlice';
import {
  getVariationMasterListApi,
  getVariationMasterListReset,
} from '../redux/slice/variationMaster/getVariationMasterListSlice';
import { getWarehouseListApi, getWarehouseListReset } from '../redux/slice/warehouse/getWarehouseListSlice';
import CustomTable from './CustomTable';
import POSummary from '../molecules/POSummary';
import CustomModal from '../atoms/CustomModal';
import POItemModal from './POItemModal';
import CustomTextArea from '../atoms/CustomTextArea';
import {
  warehouseStockSearch,
  warehouseStockSearchReset,
} from '../redux/slice/warehouseStock/warehouseStockSearchSlice';
import { debounce } from 'lodash';
import CustomSwitchButton from '../atoms/CustomSwitchButton';
import ProductPackageItemModal from './ProductPackageItemModal';
import { quantityValidation } from '../utils';
import TaxDiscountForm from '../molecules/TaxDiscoutForm';

const CreateProductPackage = ({ handleAddSubmit, handleUpdateSubmit, editForm, editDetail, loadingSubmit }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [overallTotal, setOverallTotal] = useState({
    subTotal: 0,
    cgstAmount: 0,
    sgstAmount: 0,
    igstAmount: 0,
    discount: 0,
    shipping: 0,
    grandTotal: 0,
    totalTaxableAmount: 0,
  });
  ///// Local State
  const [purchaseUnitList, setPurchaseUnitList] = useState([]);
  const [warehouseList, setWarehouseList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [orderItemList, setOrderItemList] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalFormData, setModalFormData] = useState({});
  const [loading, setLoading] = useState(false);

  ///// Global State
  const getUnitListState = useSelector((state) => state.getUnitListState);
  const getWarehouseListState = useSelector((state) => state.getWarehouseListState);
  const warehouseStockSearchState = useSelector((state) => state.warehouseStockSearchState);

  const columns = [
    {
      title: 'Product',
      dataIndex: 'variantCode',
      key: 'variantCode',
      render: (variantCode, record) => (
        <Space direction='vertical' align='center'>
          <span>{variantCode}</span>
          <span style={{ color: 'green' }}>
            {record.productName} <EditFilled onClick={() => handleModal(record)} />{' '}
          </span>
        </Space>
      ),
    },
    {
      title: 'Net Unit Cost',
      dataIndex: 'netUnitCost',
      key: 'netUnitCost',
    },
    {
      title: 'Quantity',
      dataIndex: 'orderQTY',
      key: 'orderQTY',
      width: '150px',
      render: (_, record) => (
        <>
          <Space.Compact
            style={{
              width: '140px',
            }}
          >
            <CustomButton
              type='primary'
              size='large'
              onClick={() => {
                if (+record.orderQTY > 1) {
                  handleUpdateQty(+record.orderQTY - 1, record);
                }
              }}
            >
              -
            </CustomButton>
            <CustomInputField
              name='orderQTY'
              type='text'
              value={record.orderQTY}
              size='large'
              // onChange={(e) => {
              //   const inputValue = e.target.value;
              //   // Validate input: must be a number and >= 1
              //   if (inputValue === '' || (Number(inputValue) >= 1 && /^\d*$/.test(inputValue))) {
              //     handleUpdateQty(+inputValue, record);
              //   }
              // }}
              onChange={(e) => {
                const inputValue = e.target.value;
                handleUpdateQty(inputValue, record);
              }}
              onBlur={(e) => {
                const inputValue = e.target.value;
                // Validate input: must be a number and >= 1
                if (quantityValidation(inputValue)) {
                  handleUpdateQty(+inputValue, record);
                } else {
                  handleUpdateQty(0, record);
                }
              }}
            />
            <CustomButton type='primary' size='large' onClick={(e) => handleUpdateQty(+record.orderQTY + 1, record)}>
              +
            </CustomButton>
          </Space.Compact>
        </>
      ),
    },
    {
      title: 'Discount',
      dataIndex: 'discount',
      key: 'discount',
    },
    {
      title: 'IGST',
      dataIndex: 'igstAmount',
      key: 'igstAmount',
    },
    {
      title: 'SGST',
      dataIndex: 'sgstAmount',
      key: 'sgstAmount',
    },
    {
      title: 'CGST',
      dataIndex: 'cgstAmount',
      key: 'cgstAmount',
    },
    {
      title: 'Subtotal',
      dataIndex: 'subTotal',
      key: 'subTotal',
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (_, record) => (
        <Button type='primary' onClick={() => handleItemDelete(record.productVariationID)} danger>
          Delete
        </Button>
      ),
    },
  ];

  /// Callback hooks
  useEffect(() => {
    dispatch(getUnitListApi());
    dispatch(getWarehouseListApi());
    dispatch(getVariationMasterListApi());
    return () => {
      dispatch(getUnitListReset());
      dispatch(getWarehouseListReset());
      dispatch(getVariationMasterListReset());
    };
  }, []);

  useEffect(() => {
    if (getWarehouseListState.status === 'success') {
      let data = [];
      getWarehouseListState.data.map((item) => {
        data.push({
          value: item.warehouseId,
          label: item.name,
        });
      });
      setWarehouseList(data);
    }
  }, [getWarehouseListState]);

  useEffect(() => {
    if (warehouseStockSearchState.status === 'success') {
      let data = [];
      warehouseStockSearchState.data.map((item) => {
        data.push({
          value: item.productVariationID,
          details: item,
          label: item.variationCode,
        });
      });
      setProductList(data);
    }
  }, [warehouseStockSearchState]);

  useEffect(() => {
    if (getUnitListState.status === 'success') {
      let data = [];
      getUnitListState.data.map((item) => {
        data.push({
          value: item.unitId,
          label: item.unitName,
        });
      });
      setPurchaseUnitList(data);
    }
  }, [getUnitListState]);

  useEffect(() => {
    if (editForm && editDetail) {
      form.setFieldsValue({
        warehouseID: editDetail.warehouseID,
        igst: editDetail.igst,
        sgst: editDetail.sgst,
        cgst: editDetail.cgst,
        discountType: editDetail.discountType,
        discount: editDetail.discount,
        note: editDetail.note,
        packageNo: editDetail.packageNo,
        name: editDetail.name,
        packageId: editDetail.packageId,
        isActive: editDetail.isActive,
      });

      let itemList = editDetail.productPackageLines.map((item) => {
        return {
          packageLineId: item.packageLineId,
          productCode: item.productCode,
          netUnitCost: item.netUnitCost,
          productName: item.productName,
          // itemCode: item.itemCode,
          orderQTY: item.orderQTY,
          sgst: item.sgst,
          igst: item.igst,
          cgst: item.cgst,
          sgstAmount: item.sgstAmount,
          igstAmount: item.igstAmount,
          cgstAmount: item.cgstAmount,
          taxableAmount: item.taxableAmount,
          subTotal: item.subTotal,
          taxType: item.taxType,
          productVariationID: item.productVariationID,
          productCost: item.productCost,
          purchaseUnitID: item.purchaseUnitID,
          discount: item.discount,
          discountType: item.discountType,
          retailPrice: item.productPrice,
          purchaseUnit: item.purchaseUnitName,
          variantCode: item.variantCode,
          warehouseID: item.warehouseID,
          companyID: item.companyID,
          remainingQTY: item.remainingQTY,
          receivingQTY: item.receivingQTY,
          packageNo: editDetail.packageNo,
          saleUnitID: item.saleUnitID,
        };
      });
      setOrderItemList(itemList);
    }
  }, [editForm, editDetail]);

  //////

  ///// Functions

  const handleSubmit = (values) => {
    if (orderItemList.length === 0) {
      notification.error({
        message: 'Something went wrong',
        description: 'At least one product select!',
        duration: 3,
        showProgress: true,
      });
      return;
    }
    handleAddSubmit({ ...overallTotal, ...values, productPackageLines: orderItemList, packageId: 0 });
  };

  const handleUpdate = (values) => {
    if (orderItemList.length === 0) {
      notification.error({
        message: 'Something went wrong',
        description: 'At least one product select!',
        duration: 3,
        showProgress: true,
      });
      return;
    }
    handleUpdateSubmit({ ...overallTotal, ...values, productPackageLines: orderItemList });
  };

  const generatePPNumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const milliseconds = String(now.getMilliseconds()).padStart(3, '0');

    return `PP-${year}${month}${day}-${hours}${minutes}${seconds}-${milliseconds}`;
  };

  const handleTaxCalculation = (find, orderQTY) => {
    let taxRate = +find.cgst + +find.igst + +find.sgst;
    let productCost = +find.productCost;
    let netUnitCost = 0;
    if (find.discount) {
      let discount = +find.discount;
      netUnitCost =
        find.discountType === 'Fixed' ? productCost - discount : productCost - (productCost * discount) / 100;
    } else {
      netUnitCost = productCost;
    }

    if (find.taxType === 'Inclusive') {
      netUnitCost = netUnitCost / (taxRate / 100 + 1); // Excluded cost
    }

    let totalAmount = netUnitCost * orderQTY; // Total Order Qty

    //// Taxable amount
    let cgstAmount = (totalAmount * find.cgst) / 100;
    let igstAmount = (totalAmount * find.igst) / 100;
    let sgstAmount = (totalAmount * find.sgst) / 100;
    let taxableAmount = sgstAmount + igstAmount + cgstAmount;
    let subTotal = totalAmount + taxableAmount;
    // console.log(totalAmount, taxableAmount, subTotal);
    // Round only the final results
    return {
      netUnitCost: +netUnitCost.toFixed(2),
      cgstAmount: +cgstAmount.toFixed(2),
      igstAmount: +igstAmount.toFixed(2),
      sgstAmount: +sgstAmount.toFixed(2),
      taxableAmount: +taxableAmount.toFixed(2),
      subTotal: +subTotal.toFixed(2),
    };
  };

  /////////////////////////////

  const calculateOverallTotal = () => {
    let overall_subtotal = 0;
    orderItemList.map((item) => {
      overall_subtotal += item.subTotal;
    });
    let discount = form.getFieldValue('discount');
    let discountType = form.getFieldValue('discountType');
    let igst = form.getFieldValue('igst');
    let sgst = form.getFieldValue('sgst');
    let cgst = form.getFieldValue('cgst');

    let discountCalculate = 0;
    if (discount) {
      discountCalculate = discountType === 'Fixed' ? +discount : (overall_subtotal * +discount) / 100;
    }

    let netSubtotal = overall_subtotal - discountCalculate;

    //// Taxable amount
    let cgstAmount = (netSubtotal * cgst) / 100;
    let igstAmount = (netSubtotal * igst) / 100;
    let sgstAmount = (netSubtotal * sgst) / 100;
    let taxableAmount = sgstAmount + igstAmount + cgstAmount;
    let overall_grandTotal = overall_subtotal + taxableAmount - discountCalculate;

    setOverallTotal({
      ...overallTotal,
      subTotal: overall_subtotal,
      grandTotal: overall_grandTotal.toFixed(2),
      totalTaxableAmount: taxableAmount.toFixed(2),
      cgstAmount: +cgstAmount.toFixed(2),
      igstAmount: +igstAmount.toFixed(2),
      sgstAmount: +sgstAmount.toFixed(2),
      discount: discountCalculate.toFixed(2),
    });
  };

  useEffect(() => {
    calculateOverallTotal();
  }, [orderItemList]);

  const handleUpdateQty = (newOrderQTY, record) => {
    const currentPoLines = form.getFieldValue('productPackageLines') || [];

    let calculateData = handleTaxCalculation(record, newOrderQTY);

    updateRowData(record, record.productVariationID, newOrderQTY, calculateData);

    const updatedPoLines = currentPoLines.map((item) =>
      item.productVariationID === record.productVariationID
        ? {
            ...item,
            orderQTY: calculateData.newOrderQTY,
            netUnitCost: calculateData.netUnitCost,
            sgstAmount: calculateData.sgstAmount,
            igstAmount: calculateData.igstAmount,
            cgstAmount: calculateData.cgstAmount,
            taxableAmount: calculateData.taxableAmount,
            subTotal: calculateData.subTotal,
          }
        : item,
    );

    // Set the updated array back to the form
    form.setFieldsValue({
      productPackageLines: updatedPoLines,
    });
  };

  // //////////////////////////////////////

  const handleProduct = (value, option) => {
    form.setFieldsValue({ productDetail: null, productSearch: null });
    let find = option.details;
    if (orderItemList.some((ele) => ele.productVariationID === find.productVariationID)) {
      notification.error({
        message: 'Something went wrong',
        description: 'This product already added.',
        duration: 3,
        showProgress: true,
      });
      return;
    } else {
      notification.success({
        message: 'Added',
        description: 'This product has added.',
        duration: 3,
        showProgress: true,
      });
    }

    let { netUnitCost, cgstAmount, igstAmount, sgstAmount, taxableAmount, subTotal } = handleTaxCalculation(find, 1);
    // const selectedOption = productList.find((option) => option.value === value);
    // const itemCode = selectedOption ? selectedOption.label : '';

    setOrderItemList([
      ...orderItemList,
      {
        key: find.productVariationID,
        packageLineId: 0,
        packageId: 0,
        productCode: find.productCode,
        netUnitCost: netUnitCost,
        productName: find.productName,
        // itemCode: find.itemCode,
        orderQTY: 1,
        sgst: find.sgst,
        igst: find.igst,
        cgst: find.cgst,
        sgstAmount: sgstAmount,
        igstAmount: igstAmount,
        cgstAmount: cgstAmount,
        taxableAmount: taxableAmount,
        subTotal: subTotal,
        taxType: find.taxType,
        productVariationID: find.productVariationID,
        productCost: find.productCost,
        saleUnitID: find.saleUnitID,
        discount: 0,
        discountType: 'Fixed',
        retailPrice: find.productPrice,
        // purchaseUnit: find.purchaseUnitName,
        variantCode: find.variationCode,
        warehouseID: form.getFieldValue('warehouseID'),
        companyID: 0,
        remainingQTY: 0,
        receivingQTY: 0,
        packageNo: form.getFieldValue('packageNo'),
      },
    ]);
  };

  const handleItemDelete = (productVariationID) => {
    let itemlist = JSON.parse(JSON.stringify(orderItemList));
    let index = itemlist.findIndex((element) => element.productVariationID === productVariationID);
    itemlist.splice(index, 1);
    setOrderItemList(itemlist);
  };

  const handleModal = (record) => {
    setModalOpen(true);
    setModalFormData(record);
  };

  const afterCloseModal = () => {
    setModalOpen(false);
    setModalFormData({});
  };

  const handleSubmitItemModal = (modalData, productVariationID, orderQTY) => {
    setModalOpen(false);
    setModalFormData({});
    let calculateData = handleTaxCalculation(modalData, orderQTY);

    updateRowData(modalData, productVariationID, orderQTY, calculateData);
  };

  const updateRowData = (modalData, productVariationID, orderQTY, calculateData) => {
    let { netUnitCost, cgstAmount, igstAmount, sgstAmount, taxableAmount, subTotal } = calculateData;
    setOrderItemList((prevState) =>
      prevState.map((item) => {
        return item.productVariationID === productVariationID
          ? {
              ...item,
              productCost: modalData.productCost,
              taxType: modalData.taxType,
              igst: modalData.igst,
              sgst: modalData.sgst,
              cgst: modalData.cgst,
              purchaseUnitID: modalData.purchaseUnitID,
              discount: modalData.discount,
              discountType: modalData.discountType,
              netUnitCost: netUnitCost,
              cgstAmount: cgstAmount,
              igstAmount: igstAmount,
              sgstAmount: sgstAmount,
              taxableAmount: taxableAmount,
              subTotal: subTotal,
              orderQTY: orderQTY,
            }
          : item;
      }),
    );
  };

  const regexOnNumber = {
    pattern: /^\d+$/,
    message: 'Must be a number',
  };

  const fetchSuggestions = useCallback(
    debounce((query) => {
      startTransition(() => {
        setLoading(true);
        dispatch(warehouseStockSearch(query));
      });
    }, 500),
    [dispatch, startTransition],
  );

  const handleSearch = (query) => {
    // setInputValue(query);
    if (query.length >= 2) {
      fetchSuggestions(query);
    } else {
      setProductList([]);
      dispatch(warehouseStockSearchReset());
    }
  };

  return (
    <>
      {modalOpen && (
        <CustomModal
          title={`Item`}
          okText='Save'
          modalOpen={modalOpen}
          afterCloseModal={afterCloseModal}
          open={modalOpen}
          onOk={afterCloseModal}
          onCancel={afterCloseModal}
          footer={null}
        >
          <ProductPackageItemModal
            purchaseUnitList={purchaseUnitList}
            modalFormData={modalFormData}
            afterCloseModal={afterCloseModal}
            handleSubmitItemModal={handleSubmitItemModal}
          />
        </CustomModal>
      )}
      <Form
        layout='vertical'
        onFinish={editForm ? handleUpdate : handleSubmit}
        name='create_product_package'
        form={form}
        initialValues={{
          igst: 0,
          sgst: 0,
          cgst: 0,
          shipping: 0,
          discount: 0,
          discountType: 'Fixed',
          packageNo: generatePPNumber(),
          isActive: true,
        }}
      >
        <Row gutter={24}>
          <Col span={8}>
            <FormField label='Product Package Number' name='packageNo'>
              <CustomInputField size='large' placeholder='Product Package Number' disabled />
            </FormField>
          </Col>
          <Col span={8}>
            <FormField
              label='Package Name'
              name='name'
              rules={[{ required: true, message: 'Please enter package name!' }]}
            >
              <CustomInputField size='large' placeholder='Package Name' />
            </FormField>
          </Col>
          <Col span={8}>
            <FormField label='Warehouse' name='warehouseID' rules={[{ required: true, message: 'Select warehouse!' }]}>
              <CustomSelectBox size='large' showSearch placeholder='Select a warehouse' options={warehouseList} />
            </FormField>
          </Col>
          <Col span={16}>
            <FormField label='Product Search' name='productSearch'>
              <CustomSelectBox
                size='large'
                showSearch
                onSearch={handleSearch}
                // notFoundContent={loading ? <Spin size='small' /> : null}
                filterOption={false}
                placeholder='Product Search By Name, Brand, Category'
                options={productList}
                onChange={handleProduct}
                allowClear
              />
            </FormField>
          </Col>
          <Col span={8}>
            <FormField label='Active/Deactive' name='isActive'>
              <CustomSwitchButton checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
            </FormField>
          </Col>
        </Row>
        <CustomTable
          columns={columns}
          dataSource={orderItemList}
          scroll={{
            x: 1000,
          }}
          pagination={{ position: ['none', 'none'] }}
        />
        <TaxDiscountForm calculateOverallTotal={calculateOverallTotal} productPackage={true} />
        <div style={{ padding: 14, display: 'flex', justifyContent: 'flex-end' }}>
          <POSummary overallTotal={overallTotal} productPackage={true} />
        </div>
        <Row>
          <Col span={12}>
            <FormField label='Note' name='note' rules={[{ message: 'Please enter Note!' }]}>
              <CustomTextArea rows={2} placeholder='Note' size='large' />
            </FormField>
          </Col>
        </Row>
        <Row gutter={[16, 16]} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Col span={4}>
            <CustomButton type='primary' htmlType='submit' size='large' block loading={loadingSubmit}>
              {editForm ? 'Update' : 'Save'}
            </CustomButton>
          </Col>
        </Row>
      </Form>
    </>
  );
};
export default CreateProductPackage;
